import type { TaskOutcome } from '@innspel/core';
import { useId, useState } from 'react';
import { Banner, Btn, Card, H, Hint, Icon } from '../../lib/index.ts';

export const UTFALL: ReadonlyArray<{ verdi: TaskOutcome; ikon: string; etikett: string }> = [
  { verdi: 'fungerte', ikon: 'checkmark-circle-outline', etikett: 'Fungerte' },
  { verdi: 'delvis', ikon: 'contrast-outline', etikett: 'Fungerte delvis' },
  { verdi: 'ikke', ikon: 'close-circle-outline', etikett: 'Fungerte ikke' },
];

export const SETNING_MAKS = 140;

export function utfallEtikett(verdi: TaskOutcome): string {
  return UTFALL.find((u) => u.verdi === verdi)?.etikett ?? verdi;
}

/**
 * 3c — «Hvordan gikk det?» i portalen. ETT spørsmål med tre lukkede valg og en valgfri
 * setning ≤ 140 — aldri en skala (tech-decisions «M3.2 (forts.)»). Samme kort for «Gjort»
 * på en oppgave og «Svar nå» på en oppfølging. Inline som «Gå ut»-bekreftelsen: portalen
 * er én side, og et kort som kommer der du trykket er lettere å finne igjen enn en modal.
 */
export function HvordanGikkDet({
  under,
  sender,
  feil,
  onLagre,
  onLukk,
}: {
  /** Hva spørsmålet gjelder — oppgavens tittel eller oppfølgingens spørsmål. */
  under: string;
  sender: boolean;
  feil?: string | null;
  onLagre: (outcome: TaskOutcome, text: string | undefined) => void;
  onLukk: () => void;
}) {
  const id = useId();
  const [valgt, setValgt] = useState<TaskOutcome | undefined>();
  const [setning, setSetning] = useState('');

  return (
    <Card testId="hvordan-gikk-det" role="group" ariaLabel="Hvordan gikk det?">
      <Hint>{under}</Hint>
      <H id={`${id}-h`}>Hvordan gikk det?</H>

      <div className="innspel-rad" data-gap="sm" role="radiogroup" aria-labelledby={`${id}-h`} data-testid="utfall-gruppe">
        {UTFALL.map((u) => {
          const aktiv = valgt === u.verdi;
          return (
            <button
              key={u.verdi}
              type="button"
              role="radio"
              aria-checked={aktiv}
              className="innspel-utfall"
              data-aktiv={aktiv ? 'true' : undefined}
              data-testid={`utfall-${u.verdi}`}
              onClick={() => setValgt(u.verdi)}
            >
              <Icon navn={u.ikon} /> {u.etikett}
            </button>
          );
        })}
      </div>

      <label className="innspel-stabel" data-gap="xs" htmlFor={`${id}-setning`}>
        <span className="innspel-hint">Vil du si én setning om det? (valgfritt)</span>
        <textarea
          id={`${id}-setning`}
          className="innspel-tekstfelt"
          data-testid="hvordan-setning"
          value={setning}
          onChange={(e) => setSetning(e.target.value)}
          maxLength={SETNING_MAKS}
          rows={3}
          placeholder="Kopien kom, men rullesjefen manglet på torsdag."
        />
      </label>

      {feil ? (
        <Banner ikon="warning-outline" tone="danger" live testId="hvordan-feil">
          Kunne ikke lagre svaret: {feil}
        </Banner>
      ) : null}

      <div className="innspel-rad" data-gap="md">
        <Btn grow variant="secondary" label="Tilbake" testId="hvordan-tilbake" onClick={onLukk} />
        <Btn
          grow
          label="Lagre"
          testId="hvordan-lagre"
          hint="Ferdig — ingen flere spørsmål nå."
          disabled={valgt === undefined || sender}
          onClick={() => {
            if (valgt === undefined) return;
            const trimmet = setning.trim();
            onLagre(valgt, trimmet.length > 0 ? trimmet : undefined);
          }}
        />
      </div>
    </Card>
  );
}
